import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

const CHUNK_SIZE = 500;

function parseIcos(text) {
  // Akceptuje: jeden řádek, čárky, středníky, tabulátory, mezery
  return text
    .split(/[\r\n,;|\t]+/)
    .map(s => s.replace(/\D/g, '').trim())
    .filter(s => s.length >= 6 && s.length <= 9);
}

async function fetchByIcos(icos) {
  const res = await fetch(`${API_URL}/companies/by-ico`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ icos }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json(); // Array of company objects
}

export default function VraceniDtb({ onClose, isDarkMode }) {
  const [icoInput, setIcoInput] = useState('');
  const [results, setResults] = useState([]); // [{ico, matches: [{owner,status,name,id}]}]
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState('');

  // Maximální počet duplicitních vlastníků v tabulce (dynamicky)
  const maxMatches = results.reduce((m, r) => Math.max(m, r.matches.length), 0);

  const handleSearch = useCallback(async () => {
    setError('');
    const inputIcos = parseIcos(icoInput);
    if (inputIcos.length === 0) {
      setError('Nebyla nalezena žádná platná IČO (6–9 číslic).');
      return;
    }

    setLoading(true);
    setProgress({ current: 0, total: inputIcos.length });
    setResults([]);

    try {
      // Unikátní IČO pro dotaz na backend (abychom nevolali stejné IČO 50×)
      const uniqueIcos = [...new Set(inputIcos)];

      // Načti po chuncích
      const icoToMatches = {}; // ico -> [{owner, status, name, id}]
      for (let i = 0; i < uniqueIcos.length; i += CHUNK_SIZE) {
        const chunk = uniqueIcos.slice(i, i + CHUNK_SIZE);
        const companies = await fetchByIcos(chunk);

        for (const c of companies) {
          const normalizedIco = (c.ico || '').replace(/\D/g, '');
          if (!normalizedIco) continue;
          if (!icoToMatches[normalizedIco]) icoToMatches[normalizedIco] = [];
          const ownerName =
            (typeof c.createdBy === 'object' && c.createdBy?.name)
              ? c.createdBy.name
              : (c.createdBy || '');
          icoToMatches[normalizedIco].push({
            id: c._id,
            name: c.name || '',
            owner: ownerName || 'Neznámý',
            status: c.status || '',
          });
        }
        setProgress({ current: Math.min(i + CHUNK_SIZE, uniqueIcos.length), total: uniqueIcos.length });
      }

      // Sestavíme výsledky v původním pořadí vstupního seznamu
      const built = inputIcos.map(ico => ({
        ico,
        matches: icoToMatches[ico] || [],
      }));

      setResults(built);
    } catch (e) {
      setError('Chyba při načítání dat: ' + e.message);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0 });
    }
  }, [icoInput]);

  const handleExportXlsx = useCallback(() => {
    if (results.length === 0) return;

    // Dynamicky sestavíme hlavičku: IČO, VLASTNÍK, STAV [, VLASTNÍK 2, STAV 2, ...]
    const header = ['IČO', 'VLASTNÍK', 'STAV'];
    for (let i = 2; i <= maxMatches; i++) {
      header.push(`VLASTNÍK ${i}`, `STAV ${i}`);
    }

    const rows = results.map(r => {
      const row = [r.ico];
      if (r.matches.length === 0) {
        row.push('NENALEZENO', '');
        for (let i = 2; i <= maxMatches; i++) row.push('', '');
      } else {
        // První záznam
        row.push(r.matches[0].owner, r.matches[0].status);
        // Další duplicity
        for (let i = 1; i < maxMatches; i++) {
          if (r.matches[i]) {
            row.push(r.matches[i].owner, r.matches[i].status);
          } else {
            row.push('', '');
          }
        }
      }
      return row;
    });

    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);

    // Šířky sloupců
    ws['!cols'] = [
      { wch: 12 }, // IČO
      { wch: 25 }, // VLASTNÍK
      { wch: 22 }, // STAV
      ...Array(Math.max(0, maxMatches - 1) * 2).fill({ wch: 22 }),
    ];

    // Tučná hlavička
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; C++) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c: C });
      if (ws[cellAddr]) {
        ws[cellAddr].s = { font: { bold: true }, fill: { fgColor: { rgb: 'DCE6F1' } } };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vrácení DTB');
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `vraceni-dtb-${dateStr}.xlsx`);
  }, [results, maxMatches]);

  const base = isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900';
  const card = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200';
  const inputCls = isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400';
  const thCls = isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700';

  const notFound = results.filter(r => r.matches.length === 0).length;
  const found = results.filter(r => r.matches.length > 0).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className={`rounded-xl shadow-2xl flex flex-col w-full max-w-5xl ${base}`} style={{ maxHeight: '92vh' }}>
        {/* Hlavička */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold">📋 Vrácení DTB</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">×</button>
        </div>

        <div className="flex flex-col flex-1 overflow-hidden px-6 py-4 gap-4">
          {/* Input */}
          <div className={`rounded-lg border p-4 space-y-3 ${card}`}>
            <div>
              <label className="block text-sm font-medium mb-1">Seznam IČO</label>
              <p className="text-xs text-gray-500 mb-2">
                Vložte IČO — jedno na řádek, nebo oddělená čárkou / středníkem. Pořadí bude zachováno.
              </p>
              <textarea
                value={icoInput}
                onChange={e => setIcoInput(e.target.value)}
                rows={6}
                placeholder={'12345678\n87654321\n11223344'}
                className={`w-full rounded border px-3 py-2 text-sm font-mono resize-y ${inputCls}`}
              />
              <div className="text-xs text-gray-400 mt-1">
                Zadáno: {parseIcos(icoInput).length} IČO
              </div>
            </div>

            <button
              onClick={handleSearch}
              disabled={loading || !icoInput.trim()}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded font-semibold text-sm"
            >
              {loading ? '⏳ Načítám...' : '🔍 Vyhledat v CRM'}
            </button>

            {loading && progress.total > 0 && (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span>Načítám {progress.current} / {progress.total} IČO</span>
                  <span>{Math.round((progress.current / progress.total) * 100)} %</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {error && <p className="text-red-500 text-sm">{error}</p>}
          </div>

          {/* Výsledky */}
          {results.length > 0 && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Souhrn + export */}
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex gap-4 text-sm">
                  <span className="text-green-600 dark:text-green-400 font-medium">✓ Nalezeno: {found}</span>
                  <span className="text-red-500 font-medium">✗ Nenalezeno: {notFound}</span>
                  <span className="text-gray-500">Celkem: {results.length}</span>
                </div>
                <button
                  onClick={handleExportXlsx}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-semibold text-sm"
                >
                  📥 Exportovat Excel
                </button>
              </div>

              {/* Tabulka */}
              <div className="overflow-auto flex-1 rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className={`text-left py-2 px-3 font-semibold border-b border-gray-200 dark:border-gray-700 ${thCls}`}>IČO</th>
                      <th className={`text-left py-2 px-3 font-semibold border-b border-gray-200 dark:border-gray-700 ${thCls}`}>VLASTNÍK</th>
                      <th className={`text-left py-2 px-3 font-semibold border-b border-gray-200 dark:border-gray-700 ${thCls}`}>STAV</th>
                      {Array.from({ length: maxMatches - 1 }, (_, i) => (
                        <React.Fragment key={i}>
                          <th className={`text-left py-2 px-3 font-semibold border-b border-gray-200 dark:border-gray-700 ${thCls}`}>VLASTNÍK {i + 2}</th>
                          <th className={`text-left py-2 px-3 font-semibold border-b border-gray-200 dark:border-gray-700 ${thCls}`}>STAV {i + 2}</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, idx) => {
                      const notFoundRow = r.matches.length === 0;
                      const rowBg = notFoundRow
                        ? (isDarkMode ? 'bg-red-950/30' : 'bg-red-50')
                        : (idx % 2 === 0
                          ? (isDarkMode ? 'bg-gray-800' : 'bg-white')
                          : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50'));

                      return (
                        <tr key={idx} className={`border-b border-gray-100 dark:border-gray-700 ${rowBg}`}>
                          <td className="py-1.5 px-3 font-mono">{r.ico}</td>
                          {notFoundRow ? (
                            <>
                              <td className="py-1.5 px-3 text-red-500 font-medium" colSpan={1 + (maxMatches - 1) * 2 + 1}>
                                NENALEZENO
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-1.5 px-3">{r.matches[0].owner}</td>
                              <td className="py-1.5 px-3">
                                {r.matches[0].status && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                    {r.matches[0].status}
                                  </span>
                                )}
                              </td>
                              {Array.from({ length: maxMatches - 1 }, (_, i) => (
                                <React.Fragment key={i}>
                                  <td className="py-1.5 px-3">{r.matches[i + 1]?.owner || ''}</td>
                                  <td className="py-1.5 px-3">
                                    {r.matches[i + 1]?.status && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                        {r.matches[i + 1].status}
                                      </span>
                                    )}
                                  </td>
                                </React.Fragment>
                              ))}
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded font-medium text-sm"
          >
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}
